import React, { useEffect, useState } from "react";
import { PaymentScheduleResponse } from "@/api";

interface PaymentScheduleEditProps {
  schedules: PaymentScheduleResponse[];

  onChange: (
    schedules: PaymentScheduleResponse[],
  ) => void;

  finalAmount: number;
}

export default function PaymentScheduleEdit({
  finalAmount,
  schedules,
  onChange,
}: PaymentScheduleEditProps) {
  const totalAmount = Number(finalAmount ?? 0);

  const [virtualSchedules, setVirtualSchedules] =
    useState<PaymentScheduleResponse[]>(schedules);

  /*
   * --------------------------------------------------
   * Synchronize when editing starts / source changes
   * --------------------------------------------------
   */

  useEffect(() => {
    setVirtualSchedules(
      schedules.map((schedule) => ({
        ...schedule,
      })),
    );
  }, [schedules]);

  /*
   * --------------------------------------------------
   * Helpers
   * --------------------------------------------------
   */

  const calculateAmount = (
    percentage: number,
  ): string => {
    if (totalAmount <= 0) {
      return "0.00";
    }

    return (
      (totalAmount * percentage) /
      100
    ).toFixed(2);
  };

  const calculatePercentage = (
    amount: string | number | null | undefined,
  ): number => {
    if (totalAmount <= 0) {
      return 0;
    }

    return (
      (Number(amount ?? 0) / totalAmount) *
      100
    );
  };

  const updateVirtualSchedules = (
    updatedSchedules: PaymentScheduleResponse[],
  ) => {
    setVirtualSchedules(updatedSchedules);
    onChange(updatedSchedules);
  };

  /*
   * --------------------------------------------------
   * Full Payment
   * --------------------------------------------------
   */

  const switchToFullPayment = () => {
    if (virtualSchedules.length === 1) {
      return;
    }

    const firstSchedule =
      virtualSchedules[0];

    if (!firstSchedule) {
      return;
    }

    const fullPayment: PaymentScheduleResponse = {
      ...firstSchedule,

      sequence: 1,
      percentage: 100,
      amount: totalAmount.toFixed(2),

      label: "Full Payment",
    };

    updateVirtualSchedules([
      fullPayment,
    ]);
  };

  /*
   * --------------------------------------------------
   * Two Installments
   * --------------------------------------------------
   */

  const switchToInstallments = () => {
    if (virtualSchedules.length === 2) {
      return;
    }

    const firstSchedule =
      virtualSchedules[0];

    if (!firstSchedule) {
      return;
    }

    const firstAmount =
      totalAmount / 2;

    const secondAmount =
      totalAmount - firstAmount;

    const firstInstallment: PaymentScheduleResponse = {
      ...firstSchedule,
      id: firstSchedule.id,
      sequence: 1,
      percentage: 50,
      amount: firstAmount.toFixed(2),

      label: "First Installment",
    };

    const secondInstallment: PaymentScheduleResponse = {
      ...firstSchedule,

      id: 0,

      sequence: 2,
      percentage: 50,
      amount: secondAmount.toFixed(2),

      label: "Second Installment",
    };

    updateVirtualSchedules([
      firstInstallment,
      secondInstallment,
    ]);
  };

  /*
   * --------------------------------------------------
   * Percentage change
   *
   * Only relevant for two installments.
   *
   * First percentage determines second percentage.
   * Both amounts are always derived from finalAmount.
   * --------------------------------------------------
   */

  const handlePercentageChange = (
    index: number,
    percentage: number,
  ) => {
    if (virtualSchedules.length !== 2) {
      return;
    }

    const safePercentage = Math.min(
      100,
      Math.max(0, percentage),
    );

    const secondPercentage =
      100 - safePercentage;

    const updated =
      virtualSchedules.map(
        (schedule, scheduleIndex) => {
          if (scheduleIndex === 0) {
            return {
              ...schedule,
              percentage: safePercentage,
              amount:
                calculateAmount(
                  safePercentage,
                ),
            };
          }

          return {
            ...schedule,
            percentage: secondPercentage,
            amount:
              calculateAmount(
                secondPercentage,
              ),
          };
        },
      );

    updateVirtualSchedules(updated);
  };

  /*
   * --------------------------------------------------
   * General field update
   * --------------------------------------------------
   */

  const updateSchedule = (
    index: number,
    patch: Partial<PaymentScheduleResponse>,
  ) => {
    const updated =
      virtualSchedules.map(
        (schedule, scheduleIndex) =>
          scheduleIndex === index
            ? {
                ...schedule,
                ...patch,
              }
            : schedule,
      );

    updateVirtualSchedules(updated);
  };


  /*
   * --------------------------------------------------
   * Render
   * --------------------------------------------------
   */

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={
              switchToFullPayment
            }
            disabled={
              virtualSchedules.length === 1
            }
            className="rounded-md border px-3 py-2 text-sm disabled:opacity-50"
          >
            Full Payment
          </button>

          <button
            type="button"
            onClick={
              switchToInstallments
            }
            disabled={
              virtualSchedules.length === 2
            }
            className="rounded-md border px-3 py-2 text-sm disabled:opacity-50"
          >
            2 Installments
          </button>
        </div>
      </div>

      {virtualSchedules.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="px-3 py-2 text-left">
                  #
                </th>

                <th className="px-3 py-2 text-left">
                  Payment
                </th>

                <th className="px-3 py-2 text-right">
                  %
                </th>

                <th className="px-3 py-2 text-right">
                  Amount
                </th>

                <th className="px-3 py-2 text-left">
                  Due Date
                </th>
              </tr>
            </thead>

            <tbody>
              {virtualSchedules.map(
                (schedule, index) => {
                  const percentage =
                    schedule.percentage ??
                    calculatePercentage(
                      schedule.amount,
                    );

                  return (
                    <tr
                      key={
                        schedule.id ||
                        `${schedule.sequence}-${index}`
                      }
                      className="border-b"
                    >
                      <td className="px-3 py-3">
                        {index + 1}
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={
                            schedule.label ?? ""
                          }
                          onChange={(event) =>
                            updateSchedule(
                              index,
                              {
                                label:
                                  event.target
                                    .value,
                              },
                            )
                          }
                          className="w-full rounded-md border px-2 py-1"
                        />
                      </td>

                      <td className="px-3 py-3 text-right">
                        {virtualSchedules.length ===
                        2 ? (
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step="1"
                              value={
                                percentage
                              }
                              onChange={(
                                event,
                              ) =>
                                handlePercentageChange(
                                  index,
                                  Number(
                                    event
                                      .target
                                      .value,
                                  ) || 0,
                                )
                              }
                              className="w-20 rounded-md border px-2 py-1 text-right"
                            />

                            <span>
                              %
                            </span>
                          </div>
                        ) : (
                          <span>
                            100%
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-right">
                        ₹
                        {Number(
                          schedule.amount ??
                            0,
                        ).toFixed(2)}
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="date"
                          value={
                            schedule.due_date
                              ? String(
                                  schedule.due_date,
                                ).slice(
                                  0,
                                  10,
                                )
                              : ""
                          }
                          onChange={(event) =>
                            updateSchedule(
                              index,
                              {
                                due_date:
                                  event.target
                                    .value ||
                                  null,
                              },
                            )
                          }
                          className="rounded-md border px-2 py-1"
                        />
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-dashed p-6 text-center text-sm text-gray-500">
          No payment schedule configured.
        </div>
      )}
    </section>
  );
}